import {
  AdminInput,
  Box,
  BrandsIcon,
  Button,
  Div,
  DropdownMenuComponent,
  ErrorToast,
  H5,
  Icon,
  Img,
  Overlay,
  P,
  Radio,
  lightTheme,
  overlayAnimationVariant,
  restaurantImage,
  useDisableBodyScroll,
  handleSetUrlSearchParams,
} from '@vgrubs/components';
import { SimpleLocationEntity } from 'components/src/types/location.types';
import 'flatpickr/dist/flatpickr.css';
import { AnimatePresence } from 'framer-motion';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useSearchParams, SetURLSearchParams } from 'react-router-dom';
import { LocationsPicker, ScrollableWrapper } from './AdminBrandsPickerStyled';
import { SimpleBrandEntity } from 'components/src/types/brand.types';

export const AdminBrandsPicker = ({
  selectedLocations,
  selectedBrans,
  setSelectedBrands,
  handleSubmit,
  persistToUrl = false,
  setSearchParams,
}: {
  selectedLocations: SimpleLocationEntity[];
  selectedBrans: SimpleBrandEntity[];
  setSelectedBrands: Dispatch<SetStateAction<SimpleBrandEntity[]>>;
  handleSubmit: () => void;
  persistToUrl?: boolean;
  setSearchParams?: SetURLSearchParams;
}) => {
  const [searchParams, internalSetSearchParams] = useSearchParams();
  const finalSetSearchParams = setSearchParams || internalSetSearchParams;
  const [dropdown, setDropdown] = useState(false);
  const [search, setSearch] = useState('');
  const selectedBrandsRef = useRef(selectedBrans);
  // Keyboard navigation state — roving-tabindex pointer into the list.
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Keep ref in sync with prop
  useEffect(() => {
    selectedBrandsRef.current = selectedBrans;
  }, [selectedBrans]);

  useDisableBodyScroll(dropdown);

  const data = selectedLocations[0]?.brands;

  const handleSelectLocation = (newItem: SimpleBrandEntity) => {
    const index = selectedBrans.findIndex((item) => item.id === newItem.id);
    if (index === -1) {
      // Item doesn't exist, so add it
      setSelectedBrands((prevIds) => {
        const newBrands = [...prevIds, newItem];
        selectedBrandsRef.current = newBrands;
        return newBrands;
      });
    } else {
      // Item exists, so remove it
      setSelectedBrands((prevIds) => {
        const newBrands = prevIds.filter((item) => item.id !== newItem.id);
        selectedBrandsRef.current = newBrands;
        return newBrands;
      });
    }
  };

  function isLocationSelected(item: SimpleBrandEntity) {
    return selectedBrans.some((location) => location.id === item.id);
  }

  function renderLocationInputPlaceholder() {
    if (selectedBrans.length === 0) {
      return 'All Brands';
    } else if (selectedBrans.length === 1) {
      return selectedBrans[0].name;
    } else return `${selectedBrans.length} Brands`;
  }

  const filteredBrands = data?.filter((brand) =>
    brand.name.toLowerCase().includes(search.toLowerCase())
  );

  // When the dropdown opens, focus the search input. Reset cursor on close.
  useEffect(() => {
    if (!dropdown) {
      setFocusedIndex(-1);
      return;
    }
    const raf = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [dropdown]);

  // Seed the roving-tabindex cursor to the first item once data is loaded.
  // Only ONE item has tabIndex=0 at a time — so Tab from a focused item
  // falls through to the Reset button below (skipping the rest of the
  // list). Arrow keys move between items within the list.
  useEffect(() => {
    if (
      dropdown &&
      filteredBrands &&
      filteredBrands.length > 0 &&
      focusedIndex === -1
    ) {
      setFocusedIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdown, filteredBrands?.length]);

  // Move DOM focus when the cursor index changes (arrow keys) OR when
  // the filtered list updates (search narrows results). rAF defers .focus()
  // until after the re-render so the targeted item ref exists.
  useEffect(() => {
    if (focusedIndex < 0) return;
    if (!filteredBrands || focusedIndex >= filteredBrands.length) return;
    const raf = requestAnimationFrame(() => {
      itemRefs.current[focusedIndex]?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [focusedIndex, filteredBrands?.length]);

  const handleListItemKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    brand: SimpleBrandEntity,
    index: number,
    count: number
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(Math.min(index + 1, count - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      // ArrowUp on the first item returns focus to the search input.
      if (index === 0) {
        setFocusedIndex(-1);
        searchInputRef.current?.focus();
      } else {
        setFocusedIndex(index - 1);
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusedIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusedIndex(count - 1);
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleSelectLocation(brand);
    }
  };

  // ArrowDown on the search input enters the list. Always set the index
  // — the focus effect (which depends on `filteredBrands.length`) catches
  // up if items haven't rendered yet.
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(0);
    }
  };

  return (
    <AnimatePresence>
      <Div $relative $height="40px" $heightSM="40px">
        <LocationsPicker
          $opened={dropdown}
          onClick={() => {
            if (selectedLocations.length === 1) {
              setDropdown((prev) => !prev);
            } else {
              ErrorToast('Please select only one location');
            }
          }}
          aria-haspopup="dialog"
          aria-expanded={dropdown}
          aria-label="Filter by brand"
        >
          <Div>{renderLocationInputPlaceholder()}</Div>

          <Icon $opacity="0.5">
            <BrandsIcon />
          </Icon>
        </LocationsPicker>

        <DropdownMenuComponent
          isOpen={dropdown}
          maxHeight="70vh"
          padding="0"
          fullWidth
        >
          <Div $p="20px">
            <AdminInput
              ref={searchInputRef}
              search
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
              onKeyDown={handleSearchKeyDown}
              placeholder="Search Brand"
            />
          </Div>
          <ScrollableWrapper
            role="listbox"
            aria-multiselectable="true"
            aria-label="Brands"
            // overflow:auto regions are auto-tabbable in Chrome, which steals
            // focus from the actual listbox options. Opt the wrapper out;
            // keyboard nav happens via ArrowDown from the search input.
            tabIndex={-1}
          >
            {filteredBrands?.length === 0 && (
              <Div
                $flex
                $center
                $column
                $height="calc(70vh - 300px)"
                $heightSM="calc(60vh - 300px)"
              >
                <Img src={restaurantImage} alt="restaurant" $width="250px" />
                <H5>No Brands</H5>
              </Div>
            )}
            {filteredBrands?.map((l, i) => (
              <Box
                key={i}
                ref={(el: HTMLDivElement | null) => {
                  itemRefs.current[i] = el;
                }}
                role="option"
                aria-selected={isLocationSelected(l)}
                // Roving tabindex: only the currently-focused item is
                // in Tab order. Tab from it falls through to Reset
                // (skipping the rest of the list). Arrow keys move
                // the cursor between items.
                tabIndex={focusedIndex === i ? 0 : -1}
                onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) =>
                  handleListItemKeyDown(e, l, i, filteredBrands.length)
                }
                $mb="8"
                $flex
                $alignCenter
                $between
                onClick={() => handleSelectLocation(l)}
                $gap="16px"
                $pointer
              >
                <div>
                  <P
                    $bold
                    $mb="0"
                    $color={isLocationSelected(l) && lightTheme.primary}
                  >
                    {l.name}
                  </P>
                </div>

                <Radio checked={isLocationSelected(l)} />
              </Box>
            ))}
          </ScrollableWrapper>

          <Div $p="20px">
            <Button
              $selected
              $gray
              $w="100%"
              $wSM="100%"
              onClick={() => setSelectedBrands([])}
              $mb="16"
            >
              Reset Selection
            </Button>
            <Button
              $selected
              $w="100%"
              $wSM="100%"
              onClick={() => {
                // Persist to URL if enabled - use ref to get latest value
                if (persistToUrl) {
                  const currentBrands = selectedBrandsRef.current;
                  const brandIds =
                    currentBrands.length > 0
                      ? currentBrands
                          .map((brand) => brand.id.toString())
                          .join(',')
                      : null;
                  handleSetUrlSearchParams(
                    'brandIds',
                    brandIds,
                    finalSetSearchParams
                  );
                }
                setDropdown((prev) => !prev);
                handleSubmit();
              }}
            >
              Confirm
            </Button>
          </Div>
        </DropdownMenuComponent>
      </Div>

      {dropdown && (
        <Overlay
          key="modalOverlay"
          variants={overlayAnimationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={() => setDropdown(false)}
        />
      )}
    </AnimatePresence>
  );
};
